import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';

// ── Axios instance ─────────────────────────────────────────────

const apiClient = axios.create({
  // Prod builds resolve same-origin (relative '') so a missing env var never
  // silently hits a dev's localhost; Caddy serves /api on the same origin. (T6.1)
  baseURL: import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? 'http://localhost:5001' : ''),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── Helper: generate a unique transaction id ───────────────────

function generateTransactionId(): string {
  return crypto.randomUUID();
}

// ── 1. Auth interceptor ────────────────────────────────────────

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── 2. Tenant-Id interceptor ───────────────────────────────────

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const user = useAuthStore.getState().user;

  // An explicit per-call X-Tenant-Id wins (e.g. creating the initial admin for
  // a just-created tenant — ADR-009 D7). Only derive from auth/store when the
  // caller hasn't set it themselves.
  if (config.headers['X-Tenant-Id']) {
    return config;
  }

  if (user) {
    // Platform admins use the selected tenant from the tenant store.
    // Tenant admins always use their own tenantId.
    const tenantId =
      user.role === 'PlatformAdmin'
        ? useTenantStore.getState().selectedTenantId ?? user.tenantId
        : user.tenantId;

    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }
  } else if (config.data?.tenantId) {
    // Pre-auth requests (login) — get tenantId from request body if present
    config.headers['X-Tenant-Id'] = config.data.tenantId;
  }

  return config;
});

// ── 3. Transaction-Id interceptor ──────────────────────────────

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers['X-Transaction-Id'] = generateTransactionId();
  return config;
});

// ── 4. Error interceptor ───────────────────────────────────────
//
// 401 handling has TWO loose ends that bit us in the live test on
// 2026-05-11:
//
//   1. Refresh-token rotation revokes the old token on use. The admin
//      page has many concurrent polling timers (BankOperationProgress
//      every 2s, list refreshes, etc). When the JWT expires, ALL of
//      them 401 within the same millisecond and EACH starts its own
//      refresh. The first wins and revokes the old refresh token;
//      every other parallel refresh now uses the just-revoked token
//      and fails with 400 — the page goes into a tailspin of cascading
//      failures even though one refresh succeeded.
//
//   2. On a genuine refresh failure (refresh token expired AND the
//      browser hadn't been touched in days) the catch block used to
//      just call logout() — which clears state but doesn't redirect.
//      The page sat there visibly logged-in, all requests 401d
//      forever, no explanation.
//
// Fix: a single shared refresh promise (refreshInFlight). The first
// 401 starts the refresh; subsequent 401s within the same tick await
// the same promise instead of racing. On genuine failure we also
// redirect to /login.

let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('no-refresh-token');
  // Bare axios (NOT apiClient) so the interceptor we're inside doesn't
  // recurse on a 401 from the refresh endpoint itself.
  const { data } = await axios.post(
    `${apiClient.defaults.baseURL}/api/auth/refresh`,
    { refreshToken },
  );
  useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
  return data.accessToken as string;
}

function sharedRefresh(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshAccessToken().finally(() => {
    // Allow the next refresh cycle to start fresh after the in-flight
    // one completes (success or failure).
    refreshInFlight = null;
  });
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string }>) => {
    if (error.response?.status === 401 && !error.config?._retry) {
      try {
        error.config!._retry = true;
        const newAccessToken = await sharedRefresh();
        error.config!.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(error.config!);
      } catch {
        // Refresh genuinely failed (no refresh token at all, refresh
        // token expired / revoked, server unreachable). Clear local
        // state and push the user to /login so they can re-authenticate
        // instead of staring at a silently-broken page.
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
    }

    // ── 403 Forbidden ────────────────────────────────────────────
    // A 403 means the user IS authenticated but lacks the permission for
    // this specific action — NOT an expired session. Do NOT refresh, log
    // out, or redirect (any of those would punish a valid session for one
    // denied call). Mark the error so callers / apiErrorMessage can show a
    // friendly "you don't have permission" message, then reject as normal.
    if (error.response?.status === 403) {
      (error as PermissionDeniedError).isPermissionDenied = true;
    }

    // ── 429 Too Many Requests ────────────────────────────────────
    // The gateway's per-IP limiter is a 600/min fixed window shared by every
    // panel in this app (ApiGatewayService/Program.cs:338-344). Until now a 429
    // fell through untagged, so each caller's own catch rendered whatever
    // permanent-sounding failure text it had — the Media tab said "Indexing
    // could not start.", which sent the admin hunting for a broken feature when
    // the truthful answer was "the window is full, try again in a moment".
    //
    // Tagged, not retried. An automatic retry would add requests to a limiter
    // that is already rejecting them, and the caller is better placed to decide:
    // a background poll should skip a tick, a button press should tell the human.
    if (error.response?.status === 429) {
      const rateLimited = error as RateLimitedError;
      rateLimited.isRateLimited = true;
      // `Retry-After` is set by the limiter (Program.cs:342-343) in delta-seconds.
      // Absent or unparseable leaves it undefined rather than guessing a number
      // that would be shown to a human as if the server had said it.
      const retryAfter = Number(error.response.headers?.['retry-after']);
      if (Number.isFinite(retryAfter) && retryAfter >= 0) {
        rateLimited.retryAfterSeconds = retryAfter;
      }
    }

    return Promise.reject(error);
  },
);

/** An AxiosError tagged by the interceptor when the response was a 403. */
export interface PermissionDeniedError extends AxiosError {
  isPermissionDenied?: boolean;
}

/** An AxiosError tagged by the interceptor when the response was a 429. */
export interface RateLimitedError extends AxiosError {
  isRateLimited?: boolean;
  /** Delta-seconds from the response's `Retry-After`, when the server sent a usable one. */
  retryAfterSeconds?: number;
}

/** True when this error is the gateway's rate limiter rejecting the call, not a real failure. */
export function isRateLimitedError(error: unknown): error is RateLimitedError {
  return Boolean((error as RateLimitedError | null)?.isRateLimited);
}

// ── Type augmentation for retry flag ───────────────────────────

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

export default apiClient;
